import type {
  Auth0Client,
  Auth0ClientOptions,
  LogoutOptions,
  RedirectLoginOptions,
  GetUserOptions,
  GetTokenSilentlyOptions,
} from '@auth0/auth0-spa-js';
import createAuth0Client from '@auth0/auth0-spa-js';

const LOGOUT_BROADCAST_CHANNEL = 'auth-logout';
const LOGOUT_MESSAGE_TYPE = 'logout';

export interface AuthClientParams {
  clientOptions: Auth0ClientOptions;
  /**
   * How long, in hours, a session can be idle (no backend interaction)
   * before the Authorization Server invalidates the session.
   */
  idleSessionLifetimeHours: number;
  redirectLoginOptions?: RedirectLoginOptions;
  sessionTimeoutOptions?: {
    onSessionExpire?: () => void;
    onSessionExtend?: () => void;
    forceIdleSessionLogout?: boolean;
  };
}

type OnRedirectCallbackHandledListener = () => void;

export class AuthClient {
  #auth0ClientPromise: Promise<Auth0Client>;

  #redirectLoginOptions: RedirectLoginOptions;

  #onRedirectCallbackHandledListeners =
    new Set<OnRedirectCallbackHandledListener>();

  #logoutBroadcastChannel: BroadcastChannel;

  constructor(params: AuthClientParams) {
    if (!params?.clientOptions) {
      throw new Error('clientOptions cannot be null');
    }
    this.#auth0ClientPromise = createAuth0Client(params.clientOptions);

    this.#redirectLoginOptions = params.redirectLoginOptions || {};

    // Set up broadcast channel for cross-tab logout synchronization
    this.#logoutBroadcastChannel = new BroadcastChannel(
      LOGOUT_BROADCAST_CHANNEL,
    );
    this.#logoutBroadcastChannel.addEventListener('message', (event) => {
      if (event.data?.type === LOGOUT_MESSAGE_TYPE) {
        this.#performLogout(event.data.logoutOptions);
      }
    });
  }

  addRedirectCallbackHandledListener(
    listener: OnRedirectCallbackHandledListener,
  ) {
    this.#onRedirectCallbackHandledListeners.add(listener);

    return () => {
      this.#onRedirectCallbackHandledListeners.delete(listener);
    };
  }

  private async getAuth0Client() {
    return this.#auth0ClientPromise;
  }

  async isAuthenticated() {
    const client = await this.getAuth0Client();
    return client.isAuthenticated();
  }

  async getTokenSilently(options?: GetTokenSilentlyOptions) {
    const client = await this.getAuth0Client();
    return client.getTokenSilently(options);
  }

  async getUser(options?: GetUserOptions) {
    const client = await this.getAuth0Client();
    return client.getUser(options);
  }

  async loginWithRedirect(options: RedirectLoginOptions = {}) {
    const client = await this.getAuth0Client();
    return client.loginWithRedirect({
      ...this.#redirectLoginOptions,
      ...options,
    });
  }

  async logout(logoutOptions?: LogoutOptions) {
    // Broadcast logout to other tabs
    this.#logoutBroadcastChannel.postMessage({
      type: LOGOUT_MESSAGE_TYPE,
      logoutOptions,
    });

    // Perform logout in current tab
    return this.#performLogout(logoutOptions);
  }

  async #performLogout(logoutOptions?: LogoutOptions) {
    const client = await this.getAuth0Client();

    /* We may have stored non-production, local analytics events in localStorage
     * to help with debugging, if that option was enabled by the client app.
     * These events should be cleared on logout so that a new user logging
     * in does not see the previous user's events.
     *
     * This is especially important if the next user is a different user,
     * such as when an admin logs out and a regular user logs in, or vice versa.
     *
     * The local storage key is defined in the Analytics module
     * but we don't want to create a dependency on that module
     * so we define the key on its own here.
     */
    const LocalStorageEventLogKey = 'local-user-event-log';

    try {
      localStorage.removeItem(LocalStorageEventLogKey);
    } catch (error) {
      // Log error but don't prevent logout
      console.warn('Failed to clear analytics debug log on logout:', error);
    }

    return client.logout(logoutOptions);
  }

  async handleRedirectCallback() {
    const client = await this.getAuth0Client();
    const result = await client.handleRedirectCallback();

    this.#onRedirectCallbackHandledListeners.forEach((listener) => listener());

    return result;
  }

  async getIdTokenClaims() {
    const client = await this.getAuth0Client();
    return client.getIdTokenClaims();
  }
}
